/**
 * ServiceSummaryPanel Component
 *
 * Displays the left panel content for service booking flow.
 * Shows meeting title, description, and service summary (selected service, provider, duration, price).
 * Used in both ServiceBookingFlow and BookingFlow for consistent display.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { MeetingType } from '../../../../services/meetingTypeService';
import {
  PublicProduct,
  PublicProvider,
  isPointsPriced,
  resolveProductPointCost,
} from '../../../../types/product';
import SocialMediaLinks from '../../../../components/SocialMediaLinks';
import { Dropdown } from '../../../../components/ui';
import HostDisplay from '../../../../components/HostDisplay';
import { useCustomerAuth } from '../../../../contexts/CustomerAuthContext';
import { planChargeForViewer } from '../../../../utils/planPricing';

interface ServiceSummaryPanelProps {
  meetingType: MeetingType;
  selectedService: PublicProduct | null;
  selectedProvider: PublicProvider | null;
  isNoPreference: boolean;
  effectiveDuration: number;
  effectivePrice: number;
  showSocialLinks?: boolean;
  /** For duration mode: show duration and location in left column */
  showDurationLocation?: boolean;
  /** Available durations for dropdown */
  availableDurations?: number[];
  /** Currently selected duration */
  selectedDuration?: number;
  /** Callback for duration change */
  onDurationChange?: (duration: number) => void;
}

/**
 * Format price for display
 */
const formatPrice = (price: number, currency: string): string => {
  if (price === 0) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(price);
};

/**
 * Format duration for display
 */
const formatDuration = (minutes: number, t: any): string => {
  if (minutes < 60) {
    return t('booking:serviceBooking.minutesShort', '{{count}} min', { count: minutes });
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) {
    return t('booking:serviceBooking.hoursShort', '{{count}} hr', { count: hours });
  }
  return t('booking:serviceBooking.hoursMinutesShort', '{{hours}} hr {{minutes}} min', {
    hours,
    minutes: remainingMinutes,
  });
};

const ServiceSummaryPanel: React.FC<ServiceSummaryPanelProps> = ({
  meetingType,
  selectedService,
  selectedProvider,
  isNoPreference,
  effectiveDuration,
  effectivePrice,
  showSocialLinks = true,
  showDurationLocation = false,
  availableDurations = [],
  selectedDuration,
  onDurationChange,
}) => {
  const { t } = useTranslation();

  /**
   * The plan this viewer holds with this host.
   *
   * The summary quotes the selected option's price on every screen after the
   * option step, so it has to answer the same two questions the option list
   * does: their OWN plan is never quoted at them, and a dearer one is quoted
   * as the difference. Answering them in one place and not the other is how
   * the same plan came to show two different numbers on two screens.
   */
  const { walletFor } = useCustomerAuth();
  const heldPlan = walletFor((meetingType as any)?.organizationId)?.subscription ?? null;
  const isMyPlan =
    selectedService?.kind === 'subscription' && heldPlan?.productId === selectedService.id;
  const planCharge =
    selectedService?.kind === 'subscription'
      ? planChargeForViewer(effectivePrice, heldPlan?.amount ?? null)
      : null;

  // Get social media display position
  // For service bookings without avatars:
  // - 'above_avatar' maps to above meeting title (before title)
  // - 'below_avatar' maps to below meeting title (since no avatar exists, show after title but before description)
  // - 'below_description' stays the same (after description)
  // - 'bottom' is handled by the parent BookingFlow (after time/location info)
  const socialMediaPosition = meetingType.hostDisplaySettings?.socialMediaDisplayPosition || 'below_avatar';

  // Helper to render social media links
  // NOTE: When position is 'bottom', we don't render here - BookingFlow handles it
  // so it appears after time/location info
  const renderSocialLinks = (additionalClassName?: string) => {
    if (!showSocialLinks) return null;
    if (socialMediaPosition === 'bottom') return null; // Let BookingFlow handle bottom position
    return (
      <SocialMediaLinks
        twitterUrl={meetingType.twitterUrl}
        facebookUrl={meetingType.facebookUrl}
        instagramUrl={meetingType.instagramUrl}
        linkedinUrl={meetingType.linkedinUrl}
        youtubeUrl={meetingType.youtubeUrl}
        lineUrl={meetingType.lineUrl}
        websiteUrl={meetingType.websiteUrl}
        socialMediaOrder={meetingType.socialMediaOrder}
        className={additionalClassName || "mt-3"}
      />
    );
  };

  return (
    <div className="space-y-6">
      {/* For duration mode: Show HostDisplay above title (just like other meeting types) */}
      {/* Provider info (host mode) only shows AFTER provider selection */}
      {/* Company logo mode shows regardless of provider selection */}
      {showDurationLocation && !selectedService && (() => {
        const hostDisplayMode = meetingType.hostDisplaySettings?.showHostDetails || 'host';
        // Company logo mode: always show (doesn't depend on provider selection)
        // Host mode: only show when a specific provider is selected (not "No Preference")
        const shouldShowHostDisplay = hostDisplayMode === 'company' ||
          (hostDisplayMode === 'host' && selectedProvider && !isNoPreference);

        return (
          <>
            {/* Social Media Links - Above Avatar (when position is 'above_avatar') */}
            {socialMediaPosition === 'above_avatar' && renderSocialLinks("mb-3")}

            {/* HostDisplay - Shows avatar/logo + name/company based on styling settings */}
            {shouldShowHostDisplay && (
              <HostDisplay
                meetingType={meetingType}
                position="above_title"
                selectedProvider={selectedProvider}
              />
            )}

            {/* Social Media Links - Below Avatar */}
            {socialMediaPosition === 'below_avatar' && renderSocialLinks("mt-3")}
          </>
        );
      })()}

      {/* For service selection mode: Social Media above title */}
      {(!showDurationLocation || selectedService) && socialMediaPosition === 'above_avatar' && renderSocialLinks("mb-3")}

      {/* Meeting Title */}
      <div>
        <h3 className="text-2xl font-bold text-secondary-900">
          {meetingType.title || t('booking:serviceBooking.defaultTitle', 'Service Booking')}
        </h3>

        {/* Social Media Links - Below Title (for service selection mode only) */}
        {(!showDurationLocation || selectedService) && socialMediaPosition === 'below_avatar' && renderSocialLinks("mt-3")}

        {meetingType.description && (
          <p className="text-sm text-secondary-600 mt-2">
            {meetingType.description}
          </p>
        )}

        {/* Social Media Links - Below Description */}
        {socialMediaPosition === 'below_description' && renderSocialLinks("mt-4")}
      </div>

      {/* Duration and Location - For duration mode (when no service selection) */}
      {showDurationLocation && !selectedService && (
        <>
          {/* Provider Info - Show when provider is selected (icon + label + value format) */}
          {selectedProvider && !isNoPreference && (
            <div className="flex items-center space-x-3">
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                style={{ color: 'var(--brand-color, #2E7AA0)' }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              <div>
                <p className="text-xs text-secondary-500 mb-1">
                  {t('booking:serviceBooking.yourProvider', 'Your Provider')}
                </p>
                <p className="text-sm font-medium text-secondary-900">
                  {selectedProvider.displayName}
                </p>
              </div>
            </div>
          )}

          {/* Duration Info */}
          <div className="flex items-start space-x-3">
            <svg
              className="h-5 w-5 mt-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              style={{ color: 'var(--brand-color, #2E7AA0)' }}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="flex-1">
              <p className="text-xs text-secondary-500 mb-1">
                {t('booking:timeSlots.durationLabel', 'Duration')}
              </p>
              <div className="text-sm font-medium text-secondary-900">
                {availableDurations.length > 1 && onDurationChange ? (
                  <Dropdown
                    value={(selectedDuration || availableDurations[0]).toString()}
                    onChange={(value) => onDurationChange(parseInt(value as string))}
                    options={availableDurations.map((duration: number) => ({
                      value: duration.toString(),
                      label: duration >= 60
                        ? `${Math.floor(duration / 60)} hr${duration > 60 ? ` ${duration % 60} min` : ''}`
                        : `${duration} min`
                    }))}
                    className="inline-block w-auto min-w-[120px]"
                  />
                ) : (
                  <span>{formatDuration(effectiveDuration, t)}</span>
                )}
              </div>
            </div>
          </div>

          {/* Location Info */}
          <div className="flex items-center space-x-3">
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              style={{ color: 'var(--brand-color, #2E7AA0)' }}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <div>
              <p className="text-xs text-secondary-500 mb-1">
                {t('booking:confirmation.location', 'Location')}
              </p>
              <p className="text-sm font-medium text-secondary-900">
                {(() => {
                  // Determine location type based on isVirtual and isInPerson flags
                  const isVirtual = meetingType.isVirtual !== false; // Default to true
                  const isInPerson = meetingType.isInPerson === true;
                  const isPhone = !isVirtual && !isInPerson;
                  const showAfterBooking = meetingType.showLocationAfterBooking === true;

                  // If location should be hidden, show type + message
                  if (showAfterBooking) {
                    if (isVirtual) {
                      return (
                        <>
                          <span className="font-medium">{t('booking:location.online', 'Online Meeting')}</span>
                          <span className="font-normal italic text-secondary-500"> ({t('booking:location.detailsAfterBooking', 'details will be shared after booking')})</span>
                        </>
                      );
                    } else if (isInPerson) {
                      return (
                        <>
                          <span className="font-medium">{t('booking:location.inPerson', 'In-Person Meeting')}</span>
                          <span className="font-normal italic text-secondary-500"> ({t('booking:location.addressAfterBooking', 'address will be shared after booking')})</span>
                        </>
                      );
                    } else if (isPhone) {
                      return (
                        <>
                          <span className="font-medium">{t('booking:location.phone', 'Phone Call')}</span>
                          <span className="font-normal italic text-secondary-500"> ({t('booking:location.numberAfterBooking', 'number will be shared after booking')})</span>
                        </>
                      );
                    }
                  }

                  // Otherwise show full details
                  if (isVirtual) {
                    return t('booking:location.online', 'Online meeting');
                  } else if (isInPerson) {
                    // Show address if available
                    return meetingType.locationDetails || t('booking:location.inPerson', 'In-person meeting');
                  } else if (isPhone) {
                    // Show phone number if available
                    const phone = meetingType.phoneNumber
                      ? `${meetingType.countryCode || '+1'} ${meetingType.phoneNumber}`
                      : t('booking:location.phone', 'Phone call');
                    return phone;
                  }

                  return t('booking:location.online', 'Online meeting'); // Fallback
                })()}
              </p>
            </div>
          </div>
        </>
      )}

      {/* Service Summary - Show when service is selected */}
      {selectedService && (
        <div className="bg-secondary-50 rounded-lg p-4 space-y-3">
          <h4 className="text-sm font-medium text-secondary-700">
            {t('booking:serviceBooking.serviceSummary', 'Service Summary')}
          </h4>

          {/* Selected Service */}
          <div className="flex items-center gap-3">
            <svg
              className="h-5 w-5 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              style={{ color: 'var(--brand-color, #2E7AA0)' }}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            <div>
              <p className="text-xs text-secondary-500">
                {t('booking:serviceBooking.service', 'Service')}
              </p>
              <p className="text-sm font-medium text-secondary-900">
                {selectedService.title}
              </p>
            </div>
          </div>

          {/* Selected Provider */}
          <div className="flex items-center gap-3">
            <svg
              className="h-5 w-5 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              style={{ color: 'var(--brand-color, #2E7AA0)' }}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
            <div>
              <p className="text-xs text-secondary-500">
                {t('booking:serviceBooking.provider', 'Provider')}
              </p>
              <p className="text-sm font-medium text-secondary-900">
                {selectedProvider
                  ? selectedProvider.displayName
                  : isNoPreference
                  ? t('booking:serviceBooking.autoAssigned', 'Auto-assigned')
                  : t('booking:serviceBooking.notSelected', 'Not selected')}
              </p>
            </div>
          </div>

          {/* Duration */}
          <div className="flex items-center gap-3">
            <svg
              className="h-5 w-5 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              style={{ color: 'var(--brand-color, #2E7AA0)' }}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div>
              <p className="text-xs text-secondary-500">
                {t('booking:serviceBooking.duration', 'Duration')}
              </p>
              <p className="text-sm font-medium text-secondary-900">
                {formatDuration(effectiveDuration, t)}
              </p>
            </div>
          </div>

          {/* Price */}
          {effectivePrice > 0 && !isMyPlan && (
            <div className="flex items-center gap-3">
              <svg
                className="h-5 w-5 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                style={{ color: 'var(--brand-color, #2E7AA0)' }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <p className="text-xs text-secondary-500">
                  {t('booking:serviceBooking.total', 'Total')}
                </p>
                <p
                  className="text-sm font-medium"
                  style={{ color: 'var(--brand-color, #2E7AA0)' }}
                >
                  {/* In the option's OWN unit. This ran everything through a
                      currency formatter, so an option costing 5 points read
                      "¥5" on the host and calendar screens — a price the host
                      never set, in a unit nothing charges. */}
                  {isPointsPriced(selectedService)
                    ? t('booking:points.balance', '{{count}} points', {
                        count: resolveProductPointCost(selectedService),
                      })
                    : selectedService.kind === 'subscription'
                      ? planCharge?.isUpgrade
                        ? t(
                            'booking:points.upgradeFor',
                            '+{{price}} / month to upgrade',
                            {
                              price: formatPrice(planCharge.amount, selectedService.currency),
                            }
                          )
                        : t('booking:serviceBooking.perMonth', '{{price}} / month', {
                            price: formatPrice(effectivePrice, selectedService.currency),
                          })
                      : formatPrice(effectivePrice, selectedService.currency)}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* NOTE: 'bottom' position social media is rendered by BookingFlow after time/location info */}
    </div>
  );
};

export default ServiceSummaryPanel;
